import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Baie coulissante', to: '/reparation-baie-coulissante-herault/' },
  { label: 'Baie qui coince' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Déblocage de baie coulissante qui coince dans l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' },
  description: 'Baie coulissante qui coince ou déraille dans l\'Hérault ? Déblocage et remise sur rail par nos techniciens. Intervention 24-48h, 7j/7.',
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Pourquoi ma baie coulissante coince-t-elle sans raison apparente ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Les causes les plus fréquentes sont l\'usure des roulettes, l\'encrassement du rail, un désalignement du vantail dû aux mouvements du bâti, ou une dilatation du cadre aluminium. Dans l\'Hérault, les écarts de température entre l\'été et l\'hiver peuvent accentuer ces phénomènes. Un diagnostic professionnel permet d\'identifier précisément l\'origine du blocage.',
      },
    },
    {
      '@type': 'Question',
      name: 'Une baie qui coince peut-elle dérailler complètement ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, et c\'est un risque à ne pas négliger. Une baie coulissante qui coince de manière répétée finit par user anormalement les roulettes et le rail. À terme, le vantail peut sortir de son rail et basculer, ce qui représente un danger immédiat pour les occupants et peut endommager gravement la menuiserie.',
      },
    },
  ],
};

const contentBlocks = [
  {
    image: '/images/baiecoince-block1.jpg',
    imageAlt: 'Baie coulissante bloquee et coincee dans l Herault',
    children: (
      <>
        <h2>Votre baie coulissante est bloquée ? Agissez vite.</h2>
        <p>
          Rien de plus frustrant qu'une baie vitrée coulissante qui refuse de s'ouvrir un matin d'été, ou pire, qui reste bloquée en position ouverte et compromet la sécurité de votre domicile. Ce problème, extrêmement fréquent dans l'Hérault — où l'air marin de Sète, Agde ou La Grande-Motte et les fortes chaleurs montpelliéraines sollicitent les menuiseries — doit être traité rapidement pour éviter une dégradation plus grave de votre installation.
        </p>
        <h3>Les causes principales d'une baie qui coince</h3>
        <p>
          Une baie coulissante peut coincer pour de multiples raisons. La plus courante est l'usure des roulettes et galets, qui perdent leur capacité de roulement et transforment le coulissement en frottement. Le rail peut également être en cause : encrassé par des poussières, du sable ou des débris, ou même légèrement déformé par les mouvements du bâti. Le désalignement du vantail est une autre cause fréquente : avec le temps, le poids de la baie et les micro-mouvements de la structure du bâtiment peuvent décaler le vantail de son axe de coulissement. Enfin, la dilatation thermique — particulièrement sensible dans l'Hérault où les amplitudes entre l'hiver et l'été peuvent être marquées — peut suffire à bloquer une baie si les jeux de fonctionnement sont trop faibles.
        </p>
      </>
    ),
  },
  {
    image: '/images/baiecoince-block2.jpg',
    imageAlt: 'Technicien DFPRO debloquant une baie coulissante dans l Herault',
    imagePosition: 'right' as const,
    children: (
      <>
        <h3>Notre méthode de déblocage</h3>
        <p>
          Face à une baie coulissante coincée, la première règle est de ne pas forcer. Une tentative de déblocage en force peut casser les roulettes, déformer le rail ou fissurer le vitrage. Nos techniciens DFPRO Hérault appliquent une méthode progressive : inspection visuelle de l'état des roulettes et du rail, vérification de l'alignement du vantail, puis déblocage en douceur à l'aide d'outils adaptés. Une fois la baie libérée, un diagnostic complet est réalisé pour identifier et traiter la cause racine du blocage — qu'il s'agisse de <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacer les roulettes</Link>, de <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparer le rail</Link> ou de <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">procéder à un réglage complet</Link>.
        </p>
        <h3>Le cas du déraillement</h3>
        <p>
          Une baie qui déraille est une situation plus grave. Le vantail sort de son rail et peut basculer. Si cela vous arrive, sécurisez immédiatement la zone et appelez-nous. Nos techniciens interviennent en urgence pour remettre le vantail sur son rail, vérifier l'état des composants et effectuer les réparations nécessaires. Un déraillement est souvent le symptôme d'une usure avancée des roulettes ou d'un rail endommagé qu'il convient de traiter sans attendre.
        </p>
      </>
    ),
  },
];

const fullWidthSections = [
  <>
    <h3>Intervention rapide dans tout l'Hérault</h3>
    <p>
      Que vous soyez à Montpellier, dans sa métropole, ou sur le littoral — Sète, Agde, Béziers, Frontignan, Palavas-les-Flots, La Grande-Motte — nos techniciens se déplacent sous 24 à 48h, 7j/7, pour débloquer votre baie coulissante. N'attendez pas que la situation s'aggrave : un simple coincement peut évoluer vers une panne plus coûteuse si elle n'est pas traitée rapidement. Appelez-nous au 09 80 80 22 42 pour un diagnostic gratuit et sans engagement.
    </p>
    <p>
      Retrouvez l'ensemble de nos prestations sur notre page <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de baie coulissante dans l'Hérault</Link>.
    </p>
  </>,
];

export default function BaieQuiCoince() {
  return (
    <>
      <SEO
        title="Baie coulissante qui coince dans l'Hérault | DFPRO"
        description="Baie coulissante bloquée ou qui coince dans l'Hérault ? Déblocage urgent 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Appelez DFPRO Hérault au 09 80 80 22 42."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/baie-coulissante-qui-coince-herault/"
      />
      <ServicePageLayout
        title="Baie coulissante qui coince ou déraille dans l'Hérault"
        subtitle="Votre baie se bloque en cours de coulissement ou refuse de s'ouvrir ? Nos techniciens interviennent en urgence dans tout l'Hérault."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        heroImage="/images/baiecoince-hero.jpg"
        heroImageAlt="Baie coulissante bloquee qui coince DFPRO Herault"
        contentBlocks={contentBlocks}
        fullWidthSections={fullWidthSections}
      />
    </>
  );
}